import { normalizeFrameFormat } from "@/lib/frame-format"
import { coerceCanvasDurationSec, durationOptionsForCanvasModel } from "@/lib/video-duration"
import { canvasMergeQualityParams, type CanvasDefaults, type CanvasMediaKind, type CanvasModel } from "@/types/canvas"

const GENERATE_AUDIO_STORAGE_KEY = "canvas-generate-audio"

export function readGenerateAudioPref(): boolean {
  if (typeof window === "undefined") return true
  const stored = window.localStorage.getItem(GENERATE_AUDIO_STORAGE_KEY)
  if (stored === null) return true
  return stored === "true"
}

export function writeGenerateAudioPref(enabled: boolean): void {
  if (typeof window === "undefined") return
  window.localStorage.setItem(GENERATE_AUDIO_STORAGE_KEY, enabled ? "true" : "false")
}

export function firstEnabled(models: CanvasModel[], kind: CanvasMediaKind): CanvasModel | null {
  return models.find((m) => m.media_kind === kind && m.enabled) ?? models.find((m) => m.media_kind === kind) ?? null
}

export function pickDefaultModel(
  models: CanvasModel[],
  kind: CanvasMediaKind,
  preferredId?: string | null,
): CanvasModel | null {
  const preferred = preferredId ? models.find((m) => m.id === preferredId && m.media_kind === kind && m.enabled) : null
  return preferred ?? firstEnabled(models, kind)
}

export function resolveModelById(
  models: CanvasModel[],
  kind: CanvasMediaKind,
  modelId: string,
  defaults: CanvasDefaults,
): CanvasModel | null {
  const found = models.find((m) => m.id === modelId && m.media_kind === kind && m.enabled)
  if (found) return found
  const preferredId = kind === "video" ? defaults.video_model_id : defaults.image_model_id
  return pickDefaultModel(models, kind, preferredId)
}

/** Keep the current ratio when the model supports it, otherwise the project format, otherwise the model's first. */
export function aspectRatioForModel(
  model: CanvasModel | null | undefined,
  currentAspect: string,
  projectFrameFormat: string,
): string {
  if (model?.aspect_ratios.includes(currentAspect)) return currentAspect
  const projectFormat = normalizeFrameFormat(projectFrameFormat)
  if (model?.aspect_ratios.includes(projectFormat)) return projectFormat
  return model?.aspect_ratios[0] ?? currentAspect
}

/** Формат, длительность и параметры качества после смены модели. adaptiveAllowed — можно ли оставить «Авто». */
export function generationSettingsForModel(
  model: CanvasModel | null | undefined,
  mediaKind: CanvasMediaKind,
  currentAspect: string,
  currentDuration: number | null,
  projectFrameFormat: string,
  savedParams: Record<string, string> | null | undefined,
  adaptiveAllowed = false,
) {
  return {
    aspectRatio: aspectRatioForModel(model, currentAspect, projectFrameFormat),
    durationSec:
      mediaKind === "video" && model
        ? coerceCanvasDurationSec(currentDuration, durationOptionsForCanvasModel(model), adaptiveAllowed)
        : null,
    modelParams: canvasMergeQualityParams(model, savedParams),
  }
}

export function defaultParamsFor(model: CanvasModel | null | undefined): Record<string, string> {
  if (!model) return {}
  const out: Record<string, string> = {}
  for (const param of model.extra_params) out[param.key] = param.default
  // generate_audio сохраняется между сессиями: применяем выбор пользователя, а не дефолт каталога
  if ("generate_audio" in out) out.generate_audio = readGenerateAudioPref() ? "true" : "false"
  return out
}
